/**
 * NDG Event Search — view.js
 *
 * Store Interactivity API unifié pour la recherche d'événements.
 * Utilisé par : event-search, booking, cta-banner (via event-search embarqué).
 */

import { store, getContext, getElement, withScope } from '@wordpress/interactivity';
import { appendStoredUtm, pushBookClickEvent } from '../../js/front/utm-tracking.js';

const STORE_NS = 'ndg/event-search';

const debounceTimers = new WeakMap();
const mapInstances   = new WeakMap();

const GEO_CACHE_KEY = 'ndg_geo_coords';
const GEO_CACHE_TTL = 30 * 60 * 1000;

function getCachedCoords() {
	try {
		const raw = localStorage.getItem( GEO_CACHE_KEY );
		if ( ! raw ) return null;
		const { lat, lng, ts } = JSON.parse( raw );
		if ( Date.now() - ts > GEO_CACHE_TTL ) { localStorage.removeItem( GEO_CACHE_KEY ); return null; }
		return { lat, lng };
	} catch { return null; }
}

function setCachedCoords( lat, lng ) {
	try { localStorage.setItem( GEO_CACHE_KEY, JSON.stringify( { lat, lng, ts: Date.now() } ) ); }
	catch { /* storage full */ }
}

function getRoot( ref ) {
	return ref.closest( `[data-wp-interactive="${ STORE_NS }"]` );
}

const { state, actions } = store( STORE_NS, {

	state: {

		// Liste régulière de la page Booking : 1re page SSR + lots "Charger plus".
		// Dérivé aussi côté serveur (render.php du bloc booking) pour que la
		// liste soit dans le HTML et que l'hydratation retrouve les mêmes nœuds.
		get visibleRegularEvents() {
			const context = getContext();
			return [ ...( state.initialRegular || [] ), ...( context.extraRegular || [] ) ];
		},

	},

	actions: {

		geolocate() {
			const context = getContext();

			if ( context.isGeoActive ) {
				context.isGeoActive = false;
				context.userLat     = 0;
				context.userLng     = 0;
				context.hasSearched = false;
				context.results     = [];
				return;
			}

			if ( ! navigator.geolocation ) return;

			context.isGeoLoading = true;
			context.isGeoError   = false;

			context.query = '';
			const { ref } = getElement();
			const root  = getRoot( ref );
			const input = root?.querySelector( 'input[type="search"]' );
			if ( input ) input.value = '';

			const cached = getCachedCoords();
			if ( cached ) {
				context.userLat      = cached.lat;
				context.userLng      = cached.lng;
				context.isGeoActive  = true;
				context.isGeoLoading = false;
				root?.querySelector( 'form' )?.requestSubmit();
				return;
			}

			navigator.geolocation.getCurrentPosition(
				( { coords } ) => {
					setCachedCoords( coords.latitude, coords.longitude );
					context.userLat      = coords.latitude;
					context.userLng      = coords.longitude;
					context.isGeoActive  = true;
					context.isGeoLoading = false;
					root?.querySelector( 'form' )?.requestSubmit();
				},
				() => {
					context.isGeoLoading = false;
					context.isGeoError   = true;
				},
				{ timeout: 10000, maximumAge: 60000 }
			);
		},

		*search( event ) {
			event?.preventDefault();

			const context = getContext();
			const { ref } = getElement();
			const { query, activeCountry, activeRegion, restUrl, nonce, radiusKm, resultsLimit, userLat, userLng, redirectUrl } = context;

			const hasQ       = query && query.trim().length >= 2;
			const hasCountry = !! activeCountry;
			const hasRegion  = !! activeRegion;
			const hasCoords  = userLat !== 0 && userLng !== 0;

			if ( ! hasQ && ! hasCountry && ! hasRegion && ! hasCoords ) {
				if ( context.bookingUrl ) {
					pushBookClickEvent();
					window.location.href = appendStoredUtm( context.bookingUrl );
				}
				return;
			}

			if ( redirectUrl ) {
				const dest = new URL( redirectUrl, window.location.origin );
				if ( hasQ )       dest.searchParams.set( 'q', query.trim() );
				if ( hasCountry ) dest.searchParams.set( 'country', activeCountry );
				if ( hasRegion )  dest.searchParams.set( 'region', activeRegion );
				if ( hasCoords ) {
					dest.searchParams.set( 'lat', userLat );
					dest.searchParams.set( 'lng', userLng );
				}
				window.location.href = dest.toString();
				return;
			}

			context.isLoading   = true;
			context.hasSearched = true;
			context.isEmpty     = false;

			try {
				const url = new URL( restUrl );
				if ( hasQ )       url.searchParams.set( 'q',            query.trim() );
				if ( hasCountry ) url.searchParams.set( 'country_code', activeCountry );
				if ( hasRegion )  url.searchParams.set( 'region_code',  activeRegion );
				if ( hasCoords ) {
					url.searchParams.set( 'lat', userLat );
					url.searchParams.set( 'lng', userLng );
				}
				url.searchParams.set( 'radius', radiusKm    );
				url.searchParams.set( 'limit',  resultsLimit );

				const response = yield fetch( url.toString(), {
					headers: { 'X-WP-Nonce': nonce },
				} );
				const data = yield response.json();

				context.results = data.events ?? [];
				context.isEmpty = context.results.length === 0;

				if ( data.center ) {
					const root  = ref ? getRoot( ref ) : null;
					const mapEl = root?.querySelector( '.ndg-booking__map' );
					const map   = mapEl ? mapInstances.get( mapEl ) : null;
					if ( map ) {
						map.setView( [ data.center.lat, data.center.lng ], 10, { animate: true } );
					}
				}

			} catch {
				context.results = [];
				context.isEmpty = true;
			} finally {
				context.isLoading = false;
			}
		},

		selectCountry() {
			const context = getContext();
			const code    = context.countryCode;

			if ( code === '' || context.activeCountry === code ) {
				context.activeCountry = '';
				context.hasSearched   = false;
				context.results       = [];
				return;
			}

			context.activeCountry = code;
			context.hasSearched   = true;

			const { ref } = getElement();
			getRoot( ref )?.querySelector( 'form' )?.requestSubmit();
		},

		// Pagination "Charger plus" (page Booking) — va chercher le lot
		// suivant via REST (offset/limit=postsPerPage) et l'ajoute à la
		// liste régulière déjà affichée. Dédoublonne par id au cas où un
		// event serait déjà visible (special events, ou lot précédent).
		*loadMoreRegular() {
			const context = getContext();
			if ( context.isLoadingMore || ! context.hasMoreRegular ) return;

			context.isLoadingMore = true;

			try {
				const url = new URL( context.restUrl );
				url.searchParams.set( 'offset', context.regularOffset );
				url.searchParams.set( 'limit',  context.postsPerPage );

				const response = yield fetch( url.toString(), {
					headers: { 'X-WP-Nonce': context.nonce },
				} );
				const data = yield response.json();

				const known = new Set( [
					...( state.initialSpecial || [] ),
					...( state.initialRegular || [] ),
					...( context.extraRegular || [] ),
				].map( ( e ) => e.id ) );

				const fresh = ( data.events || [] ).filter( ( e ) => ! known.has( e.id ) );

				context.extraRegular   = [ ...( context.extraRegular || [] ), ...fresh ];
				context.regularOffset += ( data.events || [] ).length;
				context.hasMoreRegular = context.regularOffset < ( data.total_available ?? 0 );
			} catch {
				context.hasMoreRegular = false;
			} finally {
				context.isLoadingMore = false;
			}
		},

	},

	callbacks: {

		isActiveCountry() {
			const context = getContext();
			// Pastille "Tous" (code vide) : active seulement sans filtre pays.
			if ( ! context.countryCode ) return ! context.activeCountry;
			// ?country= peut lister plusieurs pays ("AT,DE") : chacun est actif.
			return context.activeCountry
				.toUpperCase()
				.split( ',' )
				.map( ( code ) => code.trim() )
				.includes( context.countryCode );
		},

		initFromUrl() {
			const context = getContext();
			if ( context.redirectUrl ) return;

			const params = new URLSearchParams( window.location.search );
			const q       = params.get( 'q' );
			const country = params.get( 'country' );
			const region  = params.get( 'region' );
			const lat     = parseFloat( params.get( 'lat' ) );
			const lng     = parseFloat( params.get( 'lng' ) );

			if ( ! q && ! country && ! region && ! lat ) return;

			if ( q )       context.query = q;
			if ( country ) context.activeCountry = country;
			if ( region )  context.activeRegion = region;
			if ( lat && lng ) {
				context.userLat     = lat;
				context.userLng     = lng;
				context.isGeoActive = true;
			}

			const { ref } = getElement();
			requestAnimationFrame( () => {
				ref.querySelector( 'form' )?.requestSubmit();
			} );
		},

		// Scroll infini (page Booking) : le bouton "Charger plus" sert de
		// repère ; à 600 px de l'écran, le lot suivant part tout seul.
		observeLoadMore() {
			const context = getContext();
			const { ref } = getElement();
			if ( ! ref || ! ( 'IntersectionObserver' in window ) ) return;

			const observer = new IntersectionObserver(
				withScope( ( [ entry ] ) => {
					context.loadMoreInView = entry.isIntersecting;
				} ),
				{ rootMargin: '0px 0px 600px 0px' }
			);
			observer.observe( ref );
			return () => observer.disconnect();
		},

		// Relancé à chaque fin de lot : si le bouton est encore proche
		// (lot trop court pour remplir l'écran), on enchaîne.
		autoLoadMore() {
			const context = getContext();
			if ( context.loadMoreInView && context.hasMoreRegular && ! context.isLoadingMore ) {
				actions.loadMoreRegular();
			}
		},

		initMap() {
			const context = getContext();
			const { ref } = getElement();

			if ( ! window.L || ! ref ) return;

			const map = window.L.map( ref, {
				zoomControl:       true,
				scrollWheelZoom:   false,
				attributionControl: true,
			} ).setView( [ 46.5, 2.3 ], 5 );

			// CARTO exige une clé API depuis leur passage en authentification
			// obligatoire — sans elle les tuiles renvoient un visuel
			// "API key required" (https://carto.com/basemaps/apikey, gratuit).
			const keyParam = context.mapApiKey ? `?key=${ encodeURIComponent( context.mapApiKey ) }` : '';

			window.L.tileLayer(
				`https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png${ keyParam }`,
				{
					attribution: '© <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> © <a href="https://carto.com/">CARTO</a>',
					subdomains: 'abcd',
					maxZoom: 19,
				}
			).addTo( map );

			const icon = window.L.divIcon( {
				className: 'ndg-map-marker',
				html:      '<span></span>',
				iconSize:  [ 10, 10 ],
				iconAnchor: [ 5, 5 ],
			} );

			( context.allMarkers || [] ).forEach( m => {
				if ( ! m.lat || ! m.lng ) return;

				const popupHtml = `
					<div class="ndg-map-popup">
						<p class="ndg-map-popup__city">${ m.city }</p>
						${ m.venue ? `<p class="ndg-map-popup__venue">${ m.venue }</p>` : '' }
						${ m.url   ? `<a class="ndg-map-popup__link"
						                 href="${ m.url }"
						                 target="_blank"
						                 rel="noopener noreferrer">
						                 Book →
						               </a>` : '' }
					</div>`;

				window.L.marker( [ m.lat, m.lng ], { icon } )
					.bindPopup( popupHtml, { minWidth: 160, maxWidth: 240 } )
					.addTo( map );
			} );

			mapInstances.set( ref, map );
		},

		onInput( event ) {
			const context = getContext();
			const { ref } = getElement();

			context.query = event.target.value;

			clearTimeout( debounceTimers.get( ref ) );

			if ( context.query.trim().length < 2 ) {
				if ( ! context.activeCountry && ! context.activeRegion ) {
					context.results     = [];
					context.hasSearched = false;
				}
				return;
			}

			debounceTimers.set(
				ref,
				setTimeout( () => {
					ref.closest( 'form' )?.requestSubmit();
				}, 600 )
			);
		},

	},

} );
